<template>
  <!-- init 완료 전: opacity:0 으로 숨기되, min-height로 자리 확보 → 높이 플리커 방지 -->
  <div
    class="tinymce-editor-wrapper"
    :style="{
      minHeight: minHeight + 'px',
      '--tinymce-content-max-height': resizable ? 'none' : maxHeight + 'px',
      opacity: isReady ? 1 : 0,
    }"
  >
    <ClientOnly>
      <Editor
        :key="locale"
        class="tinymce-editor"
        :id="elementId"
        :model-value="boundEditorValue"
        :init="resolvedInit"
        :disabled="readonly"
        :tinymce-script-src="resolvedScriptSrc"
        @update:modelValue="onUpdateModelValue"
        @init="onInit"
        @change="onChange"
      />
    </ClientOnly>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Editor from '@tinymce/tinymce-vue';
import { normalizeHtmlBeforeEditor } from '~/composables/useHtmlNormalizeForEditor';
import { sanitizeReadonlyEditorHtml } from '~/composables/domain/plan/sanitizeReadonlyEditorHtml';
import { processAllTablesBorder } from '~/composables/useTinymceTableBorder';
import { dismissOpenOverlays } from '~/composables/useOverlayDismiss';
import { showAlert } from '~/composables/useConfirmDialog';

const { t, locale } = useI18n();

/**
 * TinyMCE init 완료 여부
 * - false(초기): 래퍼 opacity:0 → 에디터가 보이지 않지만 레이아웃은 확보
 * - true(init 후): opacity:1 → 부드럽게 나타남
 */
const isReady = ref(false);

/** onInit 에서 주입되는 TinyMCE 에디터 인스턴스 (getContent expose 용) */
let editorInstance = null;

/**
 * 에디터가 마지막으로 부모에 emit 한 값.
 * 부모 v-model echo 와 외부(조회/초기화) 주입을 구분하는 데 사용.
 */
let lastEmittedValue = null;

const config = useRuntimeConfig();
const appBaseUrl = (config.app?.baseURL || '').replace(/\/$/, '') || '';

const props = defineProps({
  /**
   * 레거시 호환을 위한 editorId
   * - 미지정 시 내부에서 랜덤 ID 생성
   */
  editorId: { type: String, default: '' },
  modelValue: { type: String, default: '' },
  /**
   * 레거시 기본값(min_height=300)과 동일 개념
   * - autoresize 플러그인 사용 시 height보다 minHeight가 핵심
   */
  minHeight: { type: Number, default: 300 },
  maxHeight: { type: Number, default: 416 },
  height: { type: [Number, String], default: 400 },
  readonly: { type: Boolean, default: false },
  menubar: { type: [Boolean, String], default: false },
  plugins: {
    type: String,
    default:
      'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table autoresize',
  },
  toolbar: {
    type: String,
    default:
      'blocks | bold underline strikethrough forecolor | link image | alignleft aligncenter alignright alignjustify | outdent indent numlist bullist | table',
  },
  /**
   * TinyMCE init 추가 옵션 (기본 옵션 위에 merge)
   * - plugins/toolbar/menubar/min_height 같은 key는 prop이 우선
   */
  init: { type: Object, default: () => ({}) },
  /**
   * TinyMCE script 경로 (기본: public/tinymce/tinymce.min.js)
   */
  tinymceScriptSrc: { type: String, default: '' },
  /** 빈 본문일 때 표시 (TinyMCE placeholder 옵션) */
  placeholder: { type: String, default: '' },
  /**
   * 에디터에 넣기 전 미완성 `<태그` 이스케이프 (AsisTinymceEditor 와 동일)
   * - @tinymce/tinymce-vue model-value 및 init 시 body 주입 모두에 적용
   */
  normalizeHtmlBeforeSet: { type: Boolean, default: true },
  /**
   * 마우스 드래그 리사이즈 모드 (업무/협업 활동·피드백 에디터 전용)
   * - true: 상태바 리사이즈 핸들 표시 + autoresize 자동 확장(상한 없음)
   * - false(기본): 상태바 숨김, autoresize 자동 확장(minHeight~maxHeight 후 내부 스크롤)
   */
  resizable: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'init', 'change']);

const elementId = props.editorId || `tinymce-${Math.random().toString(36).slice(2)}`;

const resolvedPlaceholder = computed(() => props.placeholder || t('common.editor.placeholderDefault'));

/** TinyMCE UI language — English is built-in; Korean uses bundled ko_KR.js */
const tinymceLanguageInit = computed(() => {
  if (locale.value === 'ko') {
    return {
      language: 'ko_KR',
      language_url: `${appBaseUrl}/tinymce/langs/ko_KR.js`,
    };
  }
  return {};
});

const resolvedScriptSrc = computed(() => {
  return props.tinymceScriptSrc || `${appBaseUrl}/tinymce/tinymce.min.js`;
});

const blockFormats = computed(() => t('common.editor.blockFormats'));

const normalizedModelValue = computed(() => {
  const v = props.modelValue || '';
  return props.normalizeHtmlBeforeSet ? normalizeHtmlBeforeEditor(v) : v;
});

/**
 * @tinymce/tinymce-vue 에 넘기는 값.
 * 사용자 입력 echo 로는 갱신하지 않는다.
 * (갱신 시 tinymce-vue 가 setContent 를 호출해 undo/redo 스택이 깨짐)
 * 외부에서 본문을 통째로 넣을 때만 갱신한다.
 */
const boundEditorValue = ref(normalizedModelValue.value);

function convertQuillHtml(html) {
  if (!html) return '';

  // 레거시 로직과 동일: 탭 엔티티 보호
  const protected_ = String(html).replace(/&#9;/g, '__TAB__');

  // jQuery 없이 DOM으로 파싱/변환
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${protected_}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  // ql-align-* -> text-align 인라인 스타일
  root.querySelectorAll('.ql-align-center').forEach(el => {
    el.style.textAlign = 'center';
    el.classList.remove('ql-align-center');
  });
  root.querySelectorAll('.ql-align-right').forEach(el => {
    el.style.textAlign = 'right';
    el.classList.remove('ql-align-right');
  });
  root.querySelectorAll('.ql-align-justify').forEach(el => {
    el.style.textAlign = 'justify';
    el.classList.remove('ql-align-justify');
  });

  // ql-indent-N li -> TinyMCE 방식 중첩 <ul>/<ol> (레거시 jQuery 스택 로직과 동일)
  const lists = Array.from(root.querySelectorAll('ul, ol'));
  for (const list of lists) {
    if (!root.contains(list)) continue;

    const tag = list.tagName.toLowerCase();
    const directLis = Array.from(list.children).filter(child => child.tagName.toLowerCase() === 'li');
    if (!directLis.length) continue;

    const items = directLis.map(li => {
      const m = (li.className || '').match(/ql-indent-(\d+)/);
      const level = m ? parseInt(m[1], 10) : 0;
      return { el: li, level };
    });
    if (!items.some(it => it.level > 0)) continue;

    const newRoot = doc.createElement(tag);
    const stack = [{ level: 0, ul: newRoot, lastLi: null }];

    for (const { el: liEl, level } of items) {
      const li = liEl.cloneNode(true);
      const cls = li.getAttribute('class') || '';
      const newClass = cls.replace(/\bql-indent-\d+\b\s*/g, '').trim();
      if (newClass) li.setAttribute('class', newClass);
      else li.removeAttribute('class');

      while (stack.length > 1 && stack[stack.length - 1].level > level) {
        stack.pop();
      }
      const top = stack[stack.length - 1];

      if (top.level === level) {
        top.ul.appendChild(li);
        top.lastLi = li;
      } else if (top.lastLi) {
        const nested = doc.createElement(tag);
        nested.appendChild(li);
        top.lastLi.appendChild(nested);
        stack.push({ level, ul: nested, lastLi: li });
      } else {
        top.ul.appendChild(li);
        top.lastLi = li;
      }
    }

    list.parentNode?.replaceChild(newRoot, list);
  }

  // ql-indent-N 이 li가 아닌 요소(단락 등): padding-left (레거시와 동일)
  for (let i = 1; i <= 9; i++) {
    root.querySelectorAll(`.ql-indent-${i}`).forEach(el => {
      if (el.tagName.toLowerCase() === 'li') return;
      el.style.paddingLeft = `${i}em`;
      el.classList.remove(`ql-indent-${i}`);
    });
  }

  // 테이블 data-row 속성 제거
  root.querySelectorAll('[data-row]').forEach(el => {
    el.removeAttribute('data-row');
  });

  // 나머지 ql-* 클래스 전부 제거
  root.querySelectorAll('*').forEach(el => {
    const classes = Array.from(el.classList || []);
    if (!classes.length) return;

    classes
      .filter(c => c.startsWith('ql-'))
      .forEach(c => {
        el.classList.remove(c);
      });

    if (!el.classList.length) {
      el.removeAttribute('class');
    }
  });

  domTextNewlinesToBr(root, doc);
  const result = root.innerHTML;
  return result.replace(/__TAB__/g, '&#9;');
}

/**
 * Quill 시절 저장 HTML처럼 &lt;p&gt;/&lt;strong&gt; 등 블록·인라인 요소 안에 그대로 들어 있는 줄바꿈(\r|\n)은
 * HTML 파서/에디터가 공백으로 접을 수 있음. 시각적 줄 분리를 위해 텍스트 노드만 &lt;br&gt; 로 쪼갠다.
 * pre/code/script/style/textarea 안은 예외 (코드·원문 보존).
 * &lt;p&gt;/&lt;strong&gt; 사이 공백-only \\n·\\n\\n → 줄마다 &lt;br&gt;; \\n1) … / …\\n → 앞·뒤에도 &lt;br&gt;.
 * ul/ol/li 직계·블록 요소 사이 공백-only 노드는 HTML 포맷용으로 무시 (붙여넣기 시 &lt;br&gt; 이중 반영 방지).
 * 호출: PastePreProcess, convertQuillHtml(본문 로드).
 */
function domTextNewlinesToBr(rootEl, ownerDoc) {
  if (!rootEl || !ownerDoc) return;

  const skipTags = { pre: 1, code: 1, script: 1, style: 1, textarea: 1 };

  const nlBlockTags = {
    p: 1, div: 1, h1: 1, h2: 1, h3: 1, h4: 1, h5: 1, h6: 1,
    ul: 1, ol: 1, li: 1, blockquote: 1, table: 1, tr: 1, td: 1, th: 1,
  };

  const nlIsBlockEl = node => {
    if (!node || node.nodeType !== 1) return false;
    return !!nlBlockTags[node.tagName.toLowerCase()];
  };

  const nlListFormatParent = parentEl => {
    if (!parentEl || parentEl.nodeType !== 1) return false;
    const t = parentEl.tagName.toLowerCase();
    return t === 'ul' || t === 'ol' || t === 'li';
  };

  const nlBlockFormatWhitespace = textNode => {
    if (!textNode || textNode.nodeType !== 3) return false;
    const prev = textNode.previousSibling;
    const next = textNode.nextSibling;
    return nlIsBlockEl(prev) && nlIsBlockEl(next);
  };

  const nlFragFromWhitespace = text => {
    const frag = ownerDoc.createDocumentFragment();
    const re = /\r\n|\r|\n/g;
    let pos = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > pos) {
        const sp = text.substring(pos, m.index);
        if (sp) frag.appendChild(ownerDoc.createTextNode(sp));
      }
      frag.appendChild(ownerDoc.createElement('br'));
      pos = m.index + m[0].length;
    }
    if (pos < text.length) {
      const tail = text.substring(pos);
      if (tail) frag.appendChild(ownerDoc.createTextNode(tail));
    }
    return frag;
  };

  const nlFragFromText = text => {
    const leadingNl = /^[\r\n]/.test(text);
    const trailingNl = /[\r\n]$/.test(text);
    const core = text.replace(/^[\r\n]+/, '').replace(/[\r\n]+$/, '');
    const frag = ownerDoc.createDocumentFragment();
    if (!core) {
      if (leadingNl) frag.appendChild(ownerDoc.createElement('br'));
      if (trailingNl && /[\r\n]{2,}/.test(text)) frag.appendChild(ownerDoc.createElement('br'));
      return frag;
    }
    if (leadingNl) frag.appendChild(ownerDoc.createElement('br'));
    if (/[\r\n]/.test(core)) {
      const parts = core.split(/\r\n|\r|\n/);
      for (let pi = 0; pi < parts.length; pi++) {
        frag.appendChild(ownerDoc.createTextNode(parts[pi]));
        if (pi < parts.length - 1) {
          frag.appendChild(ownerDoc.createElement('br'));
        }
      }
    } else {
      frag.appendChild(ownerDoc.createTextNode(core));
    }
    if (trailingNl) frag.appendChild(ownerDoc.createElement('br'));
    return frag;
  };

  const walk = node => {
    let child = node.firstChild;
    while (child) {
      const next = child.nextSibling;
      if (child.nodeType === 3) {
        const tv = child.nodeValue;
        if (!tv || !/[\r\n]/.test(tv)) {
          child = next;
          continue;
        }
        if (/^\s+$/.test(tv)) {
          if (nlListFormatParent(child.parentNode) || nlBlockFormatWhitespace(child)) {
            child = next;
            continue;
          }
          const wsFrag = nlFragFromWhitespace(tv);
          if (wsFrag.childNodes.length && child.parentNode) {
            child.parentNode.replaceChild(wsFrag, child);
          }
          child = next;
          continue;
        }
        const txFrag = nlFragFromText(tv);
        if (txFrag.childNodes.length && child.parentNode) {
          child.parentNode.replaceChild(txFrag, child);
        }
      } else if (child.nodeType === 1) {
        const tg = child.tagName.toLowerCase();
        if (!skipTags[tg]) {
          walk(child);
        }
      }
      child = next;
    }
  };

  walk(rootEl);
}

/**
 * autolink / 링크 대화상자 공통: 프로토콜·상대경로 없는 URL을 절대 URL로 정규화
 * - www.naver.com + '아니오' 클릭 시 /app/task/www.naver.com 으로 붙는 TinyMCE 기본 동작 방지
 * - http://, htttp:// 등 오타·비표준 프로토콜 → https:// 강제
 */
function isLikelyWebHost(part) {
  const host = String(part || '')
    .replace(/^\/+/, '')
    .split(/[/?#]/)[0];
  return /^www\./i.test(host) || /^[a-z0-9]([a-z0-9-]*\.)+[a-z]{2,}$/i.test(host);
}

function normalizeEditorLinkHref(url, defaultProtocol = 'https') {
  const trimmed = String(url || '').trim();
  if (!trimmed) return trimmed;

  // 앵커·루트/상대 경로
  if (/^(#|\/|\.\/|\.\.\/)/.test(trimmed)) return trimmed;

  // mailto
  if (/^mailto:/i.test(trimmed)) return trimmed;

  // https — 이미 정상
  if (/^https:\/\//i.test(trimmed)) return trimmed;

  // http:// → https://
  if (/^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:\/\//i, 'https://');
  }

  // 오타·비표준 프로토콜 (htttp://, htp://, http:/ 등) — 웹 호스트면 https:// 로 보정
  const schemeMatch = trimmed.match(/^([a-z][a-z0-9+.-]*):\/\/?(.*)$/i);
  if (schemeMatch) {
    const hostPart = schemeMatch[2].replace(/^\/+/, '');
    if (hostPart && isLikelyWebHost(hostPart)) {
      return `${defaultProtocol}://${hostPart}`;
    }
    return trimmed;
  }

  if (trimmed.includes('@') && !/^mailto:/i.test(trimmed)) {
    return `mailto:${trimmed}`;
  }

  if (/^www\./i.test(trimmed)) {
    return `${defaultProtocol}://${trimmed}`;
  }

  // naver.com 등 호스트 형태
  if (/^[a-z0-9]([a-z0-9-]*\.)+[a-z]{2,}(\/.*)?$/i.test(trimmed)) {
    return `${defaultProtocol}://${trimmed}`;
  }

  return trimmed;
}

/**
 * autolink 플러그인의 convertToLink 와 동일한 href 정규화 (플러그인은 내부 함수라 외부 호출 불가)
 */
function normalizeAutolinkHref(editor, trimmed) {
  const protocol = editor.options.get('link_default_protocol') || 'https';
  return normalizeEditorLinkHref(trimmed, protocol);
}

/**
 * 붙여넣기 조각(PastePostProcess 의 node)에서만: autolink_pattern 으로 판별 후 <a> 삽입.
 */
function linkifyPlainUrlsInPasteFragment(editor, root) {
  if (!root || !editor?.dom) return;

  const pattern = editor.options.get('autolink_pattern');
  if (!pattern || !(pattern instanceof RegExp)) return;

  const dom = editor.dom;
  const doc = editor.getDoc?.() || document;

  const walk = container => {
    const childNodes = Array.from(container.childNodes);
    for (const child of childNodes) {
      if (child.nodeType !== Node.TEXT_NODE) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child;
          if (String(el.nodeName).toLowerCase() === 'a' && el.getAttribute('href')) continue;
          if (el.getAttribute('contenteditable') === 'false') continue;
          walk(el);
        }
        continue;
      }

      const textNode = child;
      const raw = textNode.data;
      if (!raw || !raw.trim()) continue;

      if (dom.getParent(textNode, 'a[href]', true)) continue;

      const trimmed = raw.trim();
      if (!pattern.test(trimmed)) continue;

      const href = normalizeAutolinkHref(editor, trimmed);

      const a = dom.create('a', { href });
      const target = editor.options.get('link_default_target');
      if (typeof target === 'string' && target) {
        dom.setAttrib(a, 'target', target);
        if (target === '_blank' && !editor.options.get('allow_unsafe_link_target')) {
          dom.setAttrib(a, 'rel', 'noopener');
        }
      }
      a.textContent = trimmed;

      const parent = textNode.parentNode;
      if (!parent) continue;

      const lead = raw.indexOf(trimmed);
      if (lead === -1) continue;

      const before = raw.slice(0, lead);
      const after = raw.slice(lead + trimmed.length);

      if (!before && !after) {
        parent.replaceChild(a, textNode);
      } else {
        const frag = doc.createDocumentFragment();
        if (before) frag.appendChild(doc.createTextNode(before));
        frag.appendChild(a);
        if (after) frag.appendChild(doc.createTextNode(after));
        parent.replaceChild(frag, textNode);
      }
    }
  };

  walk(root);
}

const tableBorderStyleLabelTexts = new Set(['Border style', '테두리 스타일']);

function normalizeLabelText(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function hideTableBorderStyleFields() {
  if (typeof document === 'undefined') return;

  document.querySelectorAll('.tox-dialog .tox-label, .tox-dialog label').forEach(label => {
    if (!tableBorderStyleLabelTexts.has(normalizeLabelText(label.textContent))) return;

    const formGroup = label.closest('.tox-form__group');
    if (formGroup) {
      formGroup.style.display = 'none';
      formGroup.setAttribute('aria-hidden', 'true');
    }
  });
}

function setupHideTableBorderStyleFields(editor) {
  editor.on('OpenWindow', () => {
    hideTableBorderStyleFields();
    requestAnimationFrame(hideTableBorderStyleFields);
    setTimeout(hideTableBorderStyleFields, 0);

    const observer = new MutationObserver(hideTableBorderStyleFields);
    observer.observe(document.body, { childList: true, subtree: true });

    const disconnect = () => observer.disconnect();
    editor.once?.('CloseWindow', disconnect);
    setTimeout(disconnect, 10000);
  });
}

/* base64 문자열 길이 기준 (레거시 _editorImgCdnLimitSize 동일 ≈ 1.5MB) */
const EDITOR_IMG_CDN_LIMIT_SIZE = 1024 * 1024 * 2;

/* 레거시 _escapeHtmlAttr */
function escapeHtmlAttr(s) {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * @tinymce/tinymce-vue 는 기본적으로 change / input / undo / redo 에서만 부모 v-model 을 갱신함.
 * FileReader·CDN 비동기 후 insertContent 만으로는 해당 이벤트가 누락되어 저장 시 빈 본문이 나갈 수 있어
 * 삽입 직후 Change 를 명시적으로 발생시킴.
 */
function syncVueModelFromEditor(editor) {
  try {
    editor?.undoManager?.dispatchChange?.();
  } catch {
    /* ignore */
  }
}

/* 레거시 _isReportEditorImageFile: type 또는 확장자로 이미지 여부 판단 */
function isEditorImageFile(file) {
  if (!file) return false;
  if (file.type && file.type.indexOf('image/') === 0) return true;
  if (!file.type && file.name && /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file.name)) return true;
  return false;
}

/**
 * 레거시 _insertQuillLikeDataEmbedImg:
 * data:application/... 같은 비이미지 src 는 TinyMCE insertContent/schema 에서 제거되므로
 * DOM 에 직접 <p><img/></p> 삽입.
 */
function insertDataEmbedImg(editor, dataUrl, fileName) {
  if (!editor || !dataUrl) return;
  const doc = editor.getDoc?.();
  if (!doc) return;

  const p = doc.createElement('p');
  const img = doc.createElement('img');
  img.setAttribute('src', dataUrl);
  img.setAttribute('alt', fileName || '');
  p.appendChild(img);

  editor.focus();
  const rng = editor.selection?.getRng?.();
  if (!rng) {
    editor.getBody?.()?.appendChild(p);
    syncVueModelFromEditor(editor);
    return;
  }
  editor.undoManager?.transact?.(() => {
    rng.deleteContents();
    rng.insertNode(p);
    rng.setStartAfter(p);
    rng.setEndAfter(p);
    rng.collapse(false);
    editor.selection.setRng(rng);
  });
  editor.nodeChanged?.();
  syncVueModelFromEditor(editor);
}

/**
 * 레거시 _convertBase64ImageToUrl:
 * base64 문자열을 서버에 보내 CDN URL 로 변환. 실패 시 '' 반환.
 */
async function convertBase64ToCdnUrl(base64Str) {
  if (!base64Str) return '';
  try {
    const res = await useApi('/api/common/convertBase64ToUrl.do', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `base64Str=${encodeURIComponent(base64Str)}`,
    });
    if (res && typeof res === 'object' && typeof res.cdnUrl === 'string' && res.cdnUrl) {
      return res.cdnUrl;
    }
    return '';
  } catch (e) {
    console.error('[TinymceEditor] convertBase64ToCdnUrl error:', e);
    return '';
  }
}

/**
 * 레거시 _replaceBase64ImagesInEditor:
 * 본문에 남아있는 data:image/... 이미지를 CDN URL 로 치환 (초기 로드 콘텐츠 대상).
 * - data-uploading="Y": 진행 중 (중복 요청 방지)
 * - data-cdn-failed="Y": 1회 실패 → NodeChange 가 잦은 이벤트라 무한 재시도 방지 (저장 시 정리)
 * - 크기 초과 알림은 같은 스캔에서 한 번만
 */
function replaceBase64ImagesInEditor(editor) {
  if (!editor) return;
  const body = editor.getBody?.();
  if (!body) return;

  const imgs = body.querySelectorAll('img[src^="data:image/"]');
  let alertedOversize = false;
  imgs.forEach(img => {
    const src = img.getAttribute('src');
    if (!src) return;
    if (img.getAttribute('data-uploading') === 'Y') return;
    if (img.getAttribute('data-cdn-failed') === 'Y') return;

    if (src.length > EDITOR_IMG_CDN_LIMIT_SIZE) {
      if (!alertedOversize) {
        showAlert(t('common.notice'), t('common.editor.imageSizeLimit'));
        alertedOversize = true;
      }
      img.remove();
      return;
    }

    img.setAttribute('data-uploading', 'Y');
    convertBase64ToCdnUrl(src)
      .then(cdnUrl => {
        if (cdnUrl) {
          img.setAttribute('src', cdnUrl);
        } else {
          img.setAttribute('data-cdn-failed', 'Y');
        }
        img.removeAttribute('data-uploading');
      })
      .catch(() => {
        img.setAttribute('data-cdn-failed', 'Y');
        img.removeAttribute('data-uploading');
      });
  });
}

/**
 * 레거시 _openImageFileDialogAndInsert:
 * 이미지 툴바 버튼 클릭 → OS 파일 선택창 → base64 변환 → CDN URL 변환 후 본문에 <img/> 삽입.
 * 비이미지(엑셀/PDF 등) 선택 시: DOM 직접 삽입(insertDataEmbedImg).
 */
function setupImageFilePicker(editor) {
  const openImageFileDialog = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener(
      'change',
      () => {
        const file = input.files?.[0];
        document.body.removeChild(input);
        if (!file) return;

        if (file.size > EDITOR_IMG_CDN_LIMIT_SIZE) {
          showAlert(t('common.notice'), t('common.editor.imageSizeLimit'));
          return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
          const result = reader.result;
          const base64Src = typeof result === 'string' ? result : '';
          if (!base64Src) return;

          /* 비이미지 data URL 은 schema 필터를 우회해 DOM 직접 삽입 */
          if (!isEditorImageFile(file)) {
            insertDataEmbedImg(editor, base64Src, file.name || '');
            return;
          }

          /* 이미지: CDN 변환 시도 → 실패 시 base64 그대로 삽입 */
          let insertSrc = base64Src;
          try {
            const cdnUrl = await convertBase64ToCdnUrl(base64Src);
            if (cdnUrl) insertSrc = cdnUrl;
          } catch {
            /* ignore – base64 fallback */
          }

          const altEsc = escapeHtmlAttr(file.name || '');
          editor.focus();
          editor.undoManager?.transact?.(() => {
            editor.insertContent(`<img src="${insertSrc}" alt="${altEsc}" />`);
          });
          syncVueModelFromEditor(editor);
        };
        reader.readAsDataURL(file);
      },
      { once: true },
    );

    input.click();
  };

  /**
   * image 플러그인 기본 버튼을 동일 이름으로 덮어써 동작만 교체 (toolbar 변경 불필요).
   * setup 시점에 등록하면 일부 빌드에서 image 플러그인이 더 늦게 로드되어 우리 등록이 다시 덮여쓰일 수 있음.
   * 따라서 setup / PreInit 두 시점 모두에서 재등록해 마지막에 우리 핸들러가 최종으로 남도록 보장.
   */
  const registerImageButton = () => {
    editor.ui.registry.addButton('image', {
      icon: 'image',
      tooltip: t('common.editor.image'),
      onAction: openImageFileDialog,
    });
  };
  registerImageButton();
  editor.on('PreInit', registerImageButton);
  /* 메뉴바를 쓰는 경우 메뉴의 이미지 항목 클릭 시에도 동일 동작이 되도록 menu item 도 동시 등록 */
  editor.ui.registry.addMenuItem('image', {
    icon: 'image',
    text: t('common.editor.image'),
    onAction: openImageFileDialog,
  });
}

/**
 * 이미지 플러그인 / 사용자의 드래그-드롭이 만든 blob:URL 이 본문에 직접 들어가는 경우가 있음.
 * blob URL 은 다른 탭/세션에서는 깨지므로 base64 로 다시 읽어 CDN URL 로 치환.
 */
function replaceBlobImagesInEditor(editor) {
  if (!editor) return;
  const body = editor.getBody?.();
  if (!body) return;

  const imgs = body.querySelectorAll('img[src^="blob:"]');
  let alertedOversize = false;
  imgs.forEach(img => {
    const src = img.getAttribute('src');
    if (!src) return;
    if (img.getAttribute('data-uploading') === 'Y') return;
    if (img.getAttribute('data-cdn-failed') === 'Y') return;

    img.setAttribute('data-uploading', 'Y');
    fetch(src)
      .then(r => r.blob())
      .then(
        blob =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }),
      )
      .then(async dataUrl => {
        if (typeof dataUrl !== 'string') return;
        if (dataUrl.length > EDITOR_IMG_CDN_LIMIT_SIZE) {
          if (!alertedOversize) {
            showAlert(t('common.notice'), t('common.editor.imageSizeLimit'));
            alertedOversize = true;
          }
          img.remove();
          return;
        }
        const cdnUrl = await convertBase64ToCdnUrl(dataUrl);
        if (cdnUrl) {
          img.setAttribute('src', cdnUrl);
        } else {
          /* CDN 실패: base64 로 대체하되 같은 이미지 재시도 방지 플래그 부여 */
          img.setAttribute('src', dataUrl);
          img.setAttribute('data-cdn-failed', 'Y');
        }
      })
      .catch(() => {
        img.setAttribute('data-cdn-failed', 'Y');
      })
      .finally(() => {
        img.removeAttribute('data-uploading');
      });
  });
}

function applyReadonlyTablePresentation(body) {
  if (!body) return;
  body.querySelectorAll('table').forEach(table => {
    const borderAttr = table.getAttribute('border');
    const isBorderless = borderAttr == null || borderAttr === '' || String(borderAttr).trim() === '0';
    if (!isBorderless) return;
    if (borderAttr == null || borderAttr === '') {
      table.setAttribute('border', '0');
    }
    if (!table.classList.contains('mce-item-table')) {
      table.classList.add('mce-item-table');
    }
  });
}

function applyTableGuideClass(editor) {
  const body = editor.getBody?.();
  if (!body) return;
  if (props.readonly) {
    // 레거시 TinyMCE init 과 동일: borderless 표에 mce-item-table + border=0 → content.min.css 점선 테두리
    applyReadonlyTablePresentation(body);
    return;
  }
  processAllTablesBorder(body);
}

function legacySetup(editor) {
  let isPasting = false;
  setupHideTableBorderStyleFields(editor);
  setupImageFilePicker(editor);

  /* 색상·제목 등 텍스트 입력 없이 서식만 바꾼 경우 @tinymce/tinymce-vue 가 v-model 을 갱신하지 않아 저장이 누락됨 → 서식/명령 시 강제 동기화 */
  editor.on('ExecCommand FormatApply FormatRemove', () => {
    syncVueModelFromEditor(editor);
  });

  editor.on('BeforeSetContent', e => {
    if (!props.readonly || !e.content || typeof e.content !== 'string') return;
    e.content = sanitizeReadonlyEditorHtml(e.content);
  });

  editor.on('init', () => applyTableGuideClass(editor));
  editor.on('SetContent', () => applyTableGuideClass(editor));
  editor.on('TableModified', () => applyTableGuideClass(editor));

  editor.on('PastePreProcess', e => {
    isPasting = true;
    const html = e.content;
    if (!html || typeof html !== 'string') return;
    try {
      const doc = editor.getDoc?.();
      if (!doc) return;
      const div = doc.createElement('div');
      div.innerHTML = html;
      domTextNewlinesToBr(div, doc);
      e.content = div.innerHTML;
    } catch {
      /* ignore */
    }
  });
  editor.on('PastePostProcess', e => {
    linkifyPlainUrlsInPasteFragment(editor, e.node);
    isPasting = false;
  });

  /* iframe 본문 포커스 시 document click 이 발생하지 않아 Select/DatePicker 가 열린 채로 남는 문제 */
  editor.on('focus', () => {
    dismissOpenOverlays();
  });

  /* placeholder만 켠 빈 본문: 클릭 포커스 후에도 캐럿이 안 그려지는 경우 → 첫 블록으로 커서 고정 */
  editor.on('focus', () => {
    if (!editor.options.get('placeholder')) return;

    const placeCaretInFirstBlock = () => {
      const body = editor.getBody?.();
      if (!body?.getAttribute('data-mce-placeholder')) return;

      /* placeholder 보정 전에 사용자가 표 셀(td/th)을 클릭한 경우 선택 유지 (상단 첫 블록으로 덮어쓰기 방지) */
      try {
        const start = editor.selection?.getStart?.();
        if (start && editor.dom?.getParent(start, 'td,th', body)) return;
      } catch {
        /* ignore */
      }

      try {
        const first = body.firstElementChild;
        if (first && first.nodeType === Node.ELEMENT_NODE) {
          editor.selection.setCursorLocation(first, 0);
        } else if (body.firstChild) {
          editor.selection.setCursorLocation(body.firstChild, 0);
        } else {
          editor.selection.setCursorLocation(body, 0);
        }
      } catch {
        /* ignore */
      }
    };

    requestAnimationFrame(() => {
      placeCaretInFirstBlock();
      requestAnimationFrame(placeCaretInFirstBlock);
    });
  });

  editor.on('init', () => {
    const raw = props.readonly
      ? sanitizeReadonlyEditorHtml(normalizedModelValue.value)
      : normalizedModelValue.value;
    const protected_ = raw.replace(/&#9;/g, '__TAB__');
    // readonly: convertQuillHtml(domTextNewlinesToBr) 생략 — 본문 줄바꿈이 br 대량 생성됨
    const converted = props.readonly ? protected_ : convertQuillHtml(protected_);
    const safeContent = (converted || '').trim() === '' ? '<p><br></p>' : converted.replace(/&#9;/g, '\t');
    const body = editor.getBody?.();
    if (body) {
      body.innerHTML = safeContent;
      if (props.readonly) {
        applyReadonlyTablePresentation(body);
      }
    } else {
      editor.setContent(safeContent);
    }
    try {
      editor.undoManager?.clear?.();
    } catch {
      /* ignore */
    }

    /* 초기 로드된 본문에 base64 / blob 이미지가 있으면 CDN URL 로 비동기 치환 */
    replaceBase64ImagesInEditor(editor);
    replaceBlobImagesInEditor(editor);

    /* 사용자가 드래그-드롭/붙여넣기 등으로 blob 이미지를 넣은 경우 즉시 치환 */
    editor.on('NodeChange', () => {
      replaceBase64ImagesInEditor(editor);
      replaceBlobImagesInEditor(editor);
    });

    const doc = editor.getDoc?.();
    if (!doc) return;

    // 툴바 hover tooltip 제거: title 속성(브라우저 기본 tooltip)을 제거
    // TinyMCE tooltip 컨테이너는 CSS로 추가 숨김 처리
    const container = editor.getContainer?.();
    if (container) {
      container.querySelectorAll('.tox-tbtn[title]').forEach(el => {
        el.removeAttribute('title');
      });
    }

    // 초기 콘텐츠 설정 후 높이/스크롤 재계산 (처음부터 낮은 높이로 잡혀 스크롤바가 보이는 현상 방지)
    setTimeout(() => {
      editor.dispatch?.('ResizeEditor');
    }, 0);

    doc.addEventListener(
      'keydown',
      e => {
        if (e.keyCode !== 9 && e.key !== 'Tab') return;

        const sel = editor.getWin?.()?.getSelection?.();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);

        // li 안이면 들여쓰기/내어쓰기 (목록 전용 Tab / Shift+Tab)
        let node = range.startContainer;
        let liNode = null;
        while (node && node !== editor.getBody()) {
          if (node.nodeName === 'LI') {
            liNode = node;
            break;
          }
          node = node.parentNode;
        }
        if (liNode) {
          e.preventDefault();
          e.stopPropagation();
          if (e.shiftKey) {
            editor.execCommand('Outdent');
          } else {
            editor.execCommand('Indent');
          }
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (e.shiftKey) {
          // Shift+Tab: 바로 앞의 탭 문자 1개 삭제
          try {
            let container = range.startContainer;
            let offset = range.startOffset;

            // Element 노드면 자식으로 내려가기
            if (container && container.nodeType === Node.ELEMENT_NODE) {
              const childNode = container.childNodes[offset - 1];
              if (childNode && childNode.nodeType === Node.TEXT_NODE) {
                container = childNode;
                offset = childNode.textContent.length;
              }
            }

            // offset이 0이면 이전 형제 텍스트 노드 확인
            if (container && container.nodeType === Node.TEXT_NODE && offset === 0) {
              const prev = container.previousSibling;
              if (prev && prev.nodeType === Node.TEXT_NODE) {
                container = prev;
                offset = prev.textContent.length;
              }
            }

            if (container && container.nodeType === Node.TEXT_NODE && offset > 0 && container.textContent[offset - 1] === '\t') {
              editor.undoManager.transact(() => {
                const delRange = editor.getDoc().createRange();
                delRange.setStart(container, offset - 1);
                delRange.setEnd(container, offset);
                delRange.deleteContents();
              });
            }
          } catch (ex) {
            console.log(ex);
          }
          return;
        }

        // Tab 삽입 - 언두 스택에 쌓이게
        editor.undoManager.transact(() => {
          const tabNode = editor.getDoc().createTextNode('\t');
          range.deleteContents();
          range.insertNode(tabNode);
          range.setStartAfter(tabNode);
          range.setEndAfter(tabNode);
          sel.removeAllRanges();
          sel.addRange(range);
          tabNode.parentNode.normalize();
        });
      },
      true,
    );
  });

  // 저장 시 탭 문자 보존 (직렬화가 아닌 DOM에서 직접; 복사/붙여넣기·selection 조회 시에는 body 전체 치환 제외)
  editor.on('GetContent', e => {
    if (e?.format !== 'html' || e?.selection || isPasting) return;

    const body = editor.getBody?.();
    if (!body) return;

    let html = body.innerHTML;
    html = html.replace(/<br[^>]*data-mce-bogus[^>]*>/gi, '<br>');
    html = html.replace(/\s*data-mce-[a-z-]+="[^"]*"/gi, '');
    /* 이미지 CDN 변환용 내부 플래그는 저장 HTML 에 노출되지 않도록 제거 */
    html = html.replace(/\s*data-uploading="[^"]*"/gi, '');
    html = html.replace(/\s*data-cdn-failed="[^"]*"/gi, '');
    html = html.replace(/\s*class="([^"]*)"/gi, (match, classNames) => {
      const next = classNames
        .split(/\s+/)
        .filter(c => c && c !== 'tinymce-viewer-table-guide')
        .join(' ');
      return next ? ` class="${next}"` : '';
    });
    e.content = html.replace(/\t/g, '&#9;');
  });
}

/** TinyMCE iframe 본문 공통 스타일 (baseInit / resolvedInit 동일 유지) */
const tinymceContentStyle = computed(() => {
  const bodyWhiteSpace = props.readonly ? 'normal' : 'pre-wrap';
  const pStyle = props.readonly
    ? 'p { margin: 0; }'
    : 'p { margin: 0; caret-color: rgb(81, 84, 113) !important; min-height: 21px; }';

  const bodyMaxHeight = props.resizable ? 'none' : `${props.maxHeight}px`;

  return [
    `body { max-height: ${bodyMaxHeight}; overflow-y: auto !important; color: rgb(81, 84, 113); tab-size: 4; text-align: left; word-break: break-all; font-size: 14px !important; line-height: 21px !important; white-space: ${bodyWhiteSpace}; caret-color: rgb(81, 84, 113) !important; }`,
    pStyle,
    'ul, ol { margin-block-start: 0; margin-block-end: 0; }',
    'ul, ol, li { white-space: normal; }',
    /* 목록 항목에 제목(h1~h6) 서식을 적용했을 때, 마커(1./•)는 li 폰트 기준이라 작게 남는 문제 → 포함된 제목 크기에 맞춰 마커도 키움 */
    'li:has(> h1)::marker { font-size: 2em; font-weight: bold; }',
    'li:has(> h2)::marker { font-size: 1.5em; font-weight: bold; }',
    'li:has(> h3)::marker { font-size: 1.17em; font-weight: bold; }',
    'li:has(> h4)::marker { font-weight: bold; }',
    'li:has(> h5)::marker { font-size: 0.83em; font-weight: bold; }',
    'li:has(> h6)::marker { font-size: 0.67em; font-weight: bold; }',
    'img { display: block; max-width: min(100%, calc(100vw - 8px)); height: auto; box-sizing: border-box; }',
    /* placeholder ::before: 클릭 통과, 포커스 시 레이아웃에서 제거(opacity만으로는 캐럿이 가려지는 경우 있음) */
    'body[data-mce-placeholder]:not(.mce-visualblocks)::before { pointer-events: none !important; }',
    'body[data-mce-placeholder]:focus-within:not(.mce-visualblocks)::before { display: none !important; }',
    'table { border-collapse: collapse; max-width: none; box-sizing: border-box; }',
    'table:not([cellpadding]) td, table:not([cellpadding]) th { padding: 0.4rem; }',
    'table[border]:not([border="0"]):not(.tinymce-viewer-table-guide):not([data-tinymce-table-border="frame"]) td:not([style*="border"]), table[border]:not([border="0"]):not(.tinymce-viewer-table-guide):not([data-tinymce-table-border="frame"]) th:not([style*="border"]) { border-width: 1px; border-style: solid; border-color: #ccc; }',
    'table[data-tinymce-table-border="guide"].mce-item-table[border="0"] td, table[data-tinymce-table-border="guide"].mce-item-table[border="0"] th { border: 1px dashed #bbb !important; }',
    /* 가이드 표(table 자체에 인라인 border 가 없는 경우): 셀에 회색 1px solid 가 박혀 있어도 점선으로 보이도록 background-gradient 로 점선 격자 */
    'table.tinymce-viewer-table-guide { border-collapse: collapse !important; border-spacing: 0 !important; border: none !important; background-image: repeating-linear-gradient(to right, #bbb 0 3px, transparent 3px 6px), repeating-linear-gradient(to right, #bbb 0 3px, transparent 3px 6px), repeating-linear-gradient(to bottom, #bbb 0 3px, transparent 3px 6px), repeating-linear-gradient(to bottom, #bbb 0 3px, transparent 3px 6px) !important; background-position: left top, left bottom, left top, right top !important; background-size: 100% 1px, 100% 1px, 1px 100%, 1px 100% !important; background-repeat: no-repeat !important; }',
    'table.tinymce-viewer-table-guide td, table.tinymce-viewer-table-guide th { border: none !important; background-image: repeating-linear-gradient(to right, #bbb 0 3px, transparent 3px 6px), repeating-linear-gradient(to bottom, #bbb 0 3px, transparent 3px 6px) !important; background-position: left bottom, right top !important; background-size: 100% 1px, 1px 100% !important; background-repeat: no-repeat !important; background-color: transparent !important; }',
    /* content.min.css — border 없는 표: mce-item-table 또는 plain table 모두 점선 테두리 */
    'table:not([border]), table[border="0"], table[style*="border-width: 0px"], .mce-item-table:not([border]), .mce-item-table[border="0"], table:not([border]) td, table:not([border]) th, table[border="0"] td, table[border="0"] th, table[style*="border-width: 0px"] td, .mce-item-table:not([border]) td, .mce-item-table[border="0"] td, table[style*="border-width: 0px"] th, .mce-item-table:not([border]) th, .mce-item-table[border="0"] th { border: 1px dashed #bbb; }',
  ].join(' ');
});

/**
 * 높이 관련 init 옵션 (resizable 여부에 따라 분기)
 * - resizable: 상태바 리사이즈 핸들 ON, max_height 미설정(자동 확장 상한 없음)
 *   min_height는 기존과 동일하게 minHeight prop 값 그대로 사용 — 실제 최소 높이는
 *   전역 CSS(.tox-edit-area min-height:258px !important)가 그대로 결정하므로 기존 동작과 동일
 *   autoresize 플러그인은 resize 옵션이 명시적으로 지정돼 있으면 덮어쓰지 않으므로
 *   드래그 리사이즈 핸들과 autoresize 자동 확장이 함께 동작한다.
 * - 기본: 기존 autoresize 동작(minHeight~maxHeight 자동 확장 후 내부 스크롤)
 */
const heightInit = computed(() =>
  props.resizable
    ? {
        statusbar: true,
        resize: true,
        height: props.minHeight,
        min_height: props.minHeight,
        autoresize_bottom_margin: 0,
      }
    : {
        statusbar: false,
        height: props.minHeight,
        min_height: props.minHeight,
        max_height: props.maxHeight,
        autoresize_bottom_margin: 0,
      }
);

const baseInit = computed(() => ({
  base_url: `${appBaseUrl}/tinymce`,
  suffix: '.min',
  ...tinymceLanguageInit.value,
  license_key: 'gpl',
  promotion: false,
  branding: false,
  elementpath: false,
  toolbar_mode: 'sliding',
  menubar: props.menubar,
  ...heightInit.value,
  toolbar_drawer: false,
  text_patterns: false,
  /** 링크 대화상자: ‘현재창/새창’ 설정 숨김, 항상 새 창(플러그인이 target·rel 정리) */
  link_target_list: false,
  link_default_target: '_blank',
  link_default_protocol: 'https',
  /** 프로토콜 없는 URL(www.naver.com 등)에 https:// 자동 추가 — 확인 팝업 없음 */
  link_assume_external_targets: 'https',
  /**
   * 우리 도메인과 같은 호스트로 링크/이미지를 걸 때 TinyMCE가 기본값(relative_urls: true,
   * remove_script_host: true)으로 절대경로를 상대경로로 자동 변환해버려, 저장된 href에서
   * 프로토콜+도메인이 사라지는 문제가 있었음(예: https://www.sgate.biz/app/task/collaboration?lang=ko
   * → collaboration?lang=ko). 입력한 절대 URL을 그대로 유지하도록 둘 다 비활성화.
   */
  relative_urls: false,
  remove_script_host: false,
  /**
   * 링크 저장 직전 href 정규화: 프로토콜 보정 + http→https 변환 시 안내
   */
  link_attributes_postprocess: attrs => {
    if (!attrs?.href) return;

    const original = String(attrs.href).trim();
    const normalized = normalizeEditorLinkHref(original, 'https');

    if (normalized !== original && /^https:\/\//i.test(normalized)) {
      if (/^http:\/\//i.test(original)) {
        showAlert(t('common.notice'), t('common.editor.linkHttpToHttps'));
      } else {
        showAlert(t('common.notice'), t('common.editor.linkSavedAsHttps'));
      }
    }

    attrs.href = normalized;
  },
  /**
   * 이미지 자동 업로드 비활성: images_upload_url 미설정 상태에서 base64/blob 이미지에 대해
   * TinyMCE 가 자동 업로드를 시도하며 콘솔 경고를 띄움. 본 컴포넌트는 자체 CDN 변환을 사용하므로 끔.
   */
  automatic_uploads: false,
  paste_data_images: true,
  block_formats: blockFormats.value,
  content_style: tinymceContentStyle.value,
  plugins: props.plugins,
  toolbar: props.toolbar,
  placeholder: resolvedPlaceholder.value,
  setup: editor => {
    legacySetup(editor);

    const userSetup = props.init?.setup;
    if (typeof userSetup === 'function') {
      userSetup(editor);
    }
  },
}));

const resolvedInit = computed(() => {
  const merged = {
    ...props.init,
    ...baseInit.value,
  };

  // props가 최우선(merge 순서로 대부분 보장되지만, 의도 명확히)
  merged.menubar = props.menubar;
  merged.plugins = props.plugins;
  merged.toolbar = props.toolbar;
  Object.assign(merged, heightInit.value);
  merged.toolbar_drawer = false;
  merged.elementpath = false;
  merged.toolbar_mode = 'sliding';
  merged.block_formats = blockFormats.value;
  merged.content_style = tinymceContentStyle.value;
  merged.placeholder = resolvedPlaceholder.value;
  Object.assign(merged, tinymceLanguageInit.value);
  if (locale.value !== 'ko') {
    delete merged.language;
    delete merged.language_url;
  }

  return merged;
});

function onUpdateModelValue(v) {
  /* 입력·undo/redo·서식 동기화: 부모로만 올리고 boundEditorValue 는 유지 → setContent 방지 */
  lastEmittedValue = v || '';
  emit('update:modelValue', lastEmittedValue);
}

/**
 * 부모에서 본문을 통째로 교체한 경우(조회/초기화)만
 * - Editor model-value 갱신(→ tinymce-vue setContent)
 * - undo 스택 clear (해당 시점을 되돌리기 원본으로 고정)
 *
 * 에디터 emit echo(next === lastEmittedValue) 는 무시한다.
 * nextTick 플래그 방식은 TinyMCE 이벤트가 Vue 스케줄러 밖이라 타이밍이 어긋나 사용하지 않음.
 */
watch(
  () => props.modelValue,
  next => {
    if (next === lastEmittedValue) return;

    const normalized = props.normalizeHtmlBeforeSet ? normalizeHtmlBeforeEditor(next || '') : next || '';

    if (editorInstance) {
      try {
        const cur = editorInstance.getContent() || '';
        if (cur === next || cur === normalized) {
          lastEmittedValue = next;
          return;
        }
      } catch {
        /* ignore */
      }
    }

    /**
     * 입력 중에는 boundEditorValue 를 갱신하지 않음(undo/redo 보호).
     * 그래서 저장 후 부모가 '' 로 리셋해도 boundEditorValue 가 이미 '' 이면
     * tinymce-vue model-value 가 변하지 않아 setContent 가 호출되지 않음.
     * → 값이 같으면 에디터 인스턴스에 직접 반영한다.
     */
    if (boundEditorValue.value !== normalized) {
      boundEditorValue.value = normalized;
    } else if (editorInstance) {
      try {
        const safeContent = (normalized || '').trim() === '' ? '<p><br></p>' : normalized;
        editorInstance.setContent(safeContent);
        lastEmittedValue = next || '';
      } catch {
        /* ignore */
      }
    }

    setTimeout(() => {
      try {
        editorInstance?.undoManager?.clear?.();
      } catch {
        /* ignore */
      }
    }, 0);
  }
);

function onInit(_, editor) {
  editorInstance = editor;
  // autoresize + TinyMCE 내부 visibility 전환이 끝난 뒤 opacity 전환 → 높이 플리커 방지
  setTimeout(() => {
    isReady.value = true;
    emit('init', editor);
  }, 150);
}

function onChange(v) {
  emit('change', v || '');
}

/**
 * 에디터의 현재 본문을 직접 직렬화해 반환.
 * - 서식만 바꾸고 텍스트 입력이 없어 v-model 이 갱신되지 않은 경우에도 최신값 보장
 * - 저장 시 본문 수집(getCurrentActualContent 등)에서 사용
 */
function getContent() {
  if (editorInstance && typeof editorInstance.getContent === 'function') {
    return editorInstance.getContent() || '';
  }
  return normalizedModelValue.value || '';
}

defineExpose({ getContent });
</script>

<style>
/* TinyMCE 툴바 아이콘/버튼 크기 축소 (TinymceEditor 범위 내) */

/* blocks(단락/제목) 셀렉트 가로 길이 80px 고정 */
.tox-tbtn--bespoke {
  max-width: 80px !important;
  min-width: 80px !important;
  width: 80px !important;
}

.tox-tbtn {
  width: 25px !important;
}

/* 전역(img, svg) 규칙이 툴바·드롭메뉴 아이콘에만 적용 (다이얼로그 .tox-checkbox SVG의 display:none 토글을 깨지 않도록 .tox 전체에 두지 않음) */
.tox-tinymce svg,
.tox-tinymce img,
.tox-tinymce-aux .tox-menu svg,
.tox-tinymce-aux .tox-menu img,
.tox-tinymce-aux .tox-collection svg,
.tox-tinymce-aux .tox-collection img,
.tox-silver-popup-sink svg,
.tox-silver-popup-sink img {
  display: inline-block !important;
  max-width: none !important;
  height: auto !important;
}

/*
 * forecolor/backcolor 색상 스와치 체크마크
 * - 위 전역 svg display 규칙이 .tox-collection 내 svg까지 적용되어
 *   TinyMCE 기본(선택된 색상만 체크 표시)이 깨짐 → 스와치만 복원
 */
.tox div.tox-swatch:not(.tox-swatch--remove) svg {
  display: none !important;
}

.tox div.tox-swatch:not(.tox-swatch--remove).tox-collection__item--enabled svg {
  display: block !important;
}

/* 툴바 hover tooltip 숨김 */
.tox-tooltip,
.tox [role='tooltip'] {
  display: none !important;
}

/* 에디터 내용 입력 영역 높이 제한(세로 무한 확장 방지) */
.tox .tox-edit-area .mce-content-body {
  min-height: 258px !important;
  max-height: var(--tinymce-content-max-height, 416px) !important;
  overflow-y: auto !important;
}

/* 초기 렌더 시 편집 영역이 낮게 잡혀 스크롤바가 보이는 현상 방지: 최소 높이 보장 */
.tinymce-editor :deep(.tox-edit-area),
.tinymce-editor :deep(.tox-edit-area__iframe) {
  min-height: 258px !important;
}

/* @tinymce/tinymce-vue가 init 중 .tox-tinymce에 visibility:hidden을 설정하여
   나중에 visible로 전환될 때 높이가 튀는 플리커 발생 → 래퍼의 opacity로 대체 */
.tinymce-editor-wrapper .tox-tinymce {
  visibility: visible !important;
}

/* TinyMCE 기본 skin 포커스 링과 편집영역 모서리를 컴포넌트에서 강제 override */
.tinymce-editor-wrapper .tox.tox-edit-focus .tox-edit-area::before {
  border-radius: 9px !important;
}
</style>
